import { connect } from 'react-redux';
import { getHubStack } from 'features/hub/actions';
import { setTopNavColorLight } from 'features/navigation/header/actions';

import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { darkTheme, palette } from 'theme';
import { MuiThemeProvider } from '@material-ui/core/styles';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import CallsToAction from 'components/CallsToAction';
import Container from 'components/Container';
import ContentTypography from 'components/ContentTypography';
import { PageHeaderGrid, PageHeaderText } from 'components/PageHeader';
import { ResponsiveBackgroundImg } from 'components/ResponsiveImg';
import { SectionGrid } from 'components/Section';

import rgba from 'polished/lib/color/rgba';
import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';

const StickyNav = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.StickyNav
});
const StickyListItem = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.StickyListItem
});
const ListItemNumber = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.ListItemNumber
});
const StickyListItemText = loadable(() => import('components/StickyNav'), {
  resolveComponent: components => components.StickyListItemText
});
const ScrollLink = loadable(() => import('react-scroll'), {
  resolveComponent: components => components.Link
});
const ScrollElement = loadable(() => import('react-scroll'), {
  resolveComponent: components => components.Element
});
const InlineResourceCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineResourceCTA
});
const InlineBlogCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineBlogCTA
});
const InlineCTA = loadable(() => import('components/InlineCTAs'), {
  resolveComponent: components => components.InlineCTA
});
const PhotoCTA = loadable(() => import('components/CTACards'), {
  resolveComponent: components => components.PhotoCTA
});
const BoldCTA = loadable(() => import('components/CTACards'), {
  resolveComponent: components => components.BoldCTA
});

const SocialShare = loadable(() => import('components/SocialShare'));
const FooterCTAs = loadable(() => import('components/FooterCTAs'));

const Hero = styled.div`
  min-height: calc(50vh - 64px);
  padding-top: 64px;
  display: flex;
  align-items: center;
  position: relative;
  margin-top: -64px;
  margin-bottom: -5vh;
  background: ${palette.grey[900]};
  position: relative;
  z-index: 1;
`;

const HeroContainer = styled(Container)`
  position: relative;
  z-index: 1;
`;

const HubSection = styled(ScrollElement)`
  padding-top: 1em;
  margin-bottom: 8em;
`;

const HubSectionHeader = styled.div`
  padding: 10vh 0 4em;
  margin: -10vh 0 -4em 0;

  ${props =>
    props.backgroundimg &&
    css`
      position: relative;

      &:after {
        content: '';
        background-image: linear-gradient(
            to bottom,
            ${props => rgba('#fff', 1)} 0%,
            ${props => rgba('#fff', 0)} 30%
          ),
          linear-gradient(
            to top,
            ${props => rgba('#fff', 1)} 0%,
            ${props => rgba('#fff', 0)} 30%
          ),
          linear-gradient(
            to right,
            ${props => rgba('#fff', 1)} 0%,
            ${props => rgba('#fff', 0)} 30%
          ),
          linear-gradient(
            to left,
            ${props => rgba('#fff', 1)} 0%,
            ${props => rgba('#fff', 0)} 30%
          ),
          ${props =>
            props.backgroundimg
              ? `url('${props.backgroundimg}?format=pjpg&width=1500&auto=webp&quality=30')`
              : 'none'};
        background-size: ${props => (props.backgroundimg ? 'cover' : 'none')};
        background-position: 50% 50%;
        opacity: 0.15;
        mix-blend-mode: luminosity;
        width: 70vw;
        height: 200%;
        top: -25%;
        left: -50%;
        bottom: 0;
        right: 0;
        position: absolute;
        z-index: -1;
      }

      * {
        z-index: 1;
      }
    `};
`;

const HubSectionTitle = styled(Typography)`
  position: relative;
  z-index: 1;
`;

export class Hub extends React.PureComponent {
  componentWillMount() {
    this.props.setTopNavColorLight();
  }

  componentDidMount() {
    this.props.getHubStack(this.props.match.url);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.match.url !== this.props.match.url) {
      this.props.getHubStack(this.props.match.url);
    }
  }

  render() {
    const { stack, loading } = this.props;
    return !loading ? (
      <Fragment>
        <MetaTags stack={stack} />
        <SwiftypeTags
          stack={stack}
          title={stack.title}
          image={stack.hero_image.url}
          contentType="Learn"
          sections={stack.sections.map(section => section.title)}
        />
        <Hero>
          <MuiThemeProvider theme={darkTheme}>
            <HeroContainer>
              <PageHeaderGrid container>
                <Grid item xs={12} md={8}>
                  <PageHeaderText
                    heading={stack.title}
                    subheading={stack.summary}
                    theme={darkTheme}
                  />
                </Grid>
              </PageHeaderGrid>
            </HeroContainer>
          </MuiThemeProvider>
          <ResponsiveBackgroundImg
            src={stack.hero_image.url}
            defaultWidth={1500}
          />
        </Hero>

        <Container>
          <SectionGrid container spacing={32}>
            <Grid item md={3} xs={12}>
              <PrerenderSafeLazyLoad>
                <StickyNav>
                  <Paper>
                    <List component="nav">
                      <ListSubheader disableSticky>Contents</ListSubheader>
                      {stack.sections.map((section, idx) => (
                        <StickyListItem
                          button
                          key={idx}
                          component={ScrollLink}
                          activeClass="active"
                          to={section.title}
                          spy={true}
                          smooth={true}
                          duration={300}
                          offset={-50}
                        >
                          <ListItemNumber variant="overline" component="span">
                            {String(idx + 1).padStart(2, 0)}.
                          </ListItemNumber>
                          <StickyListItemText
                            primary={section.sticky_menu_title || section.title}
                          />
                        </StickyListItem>
                      ))}
                    </List>
                  </Paper>
                  <CallsToAction
                    actionItems={stack.sidebar_ctas}
                    align="flex-start"
                    stacked
                  />
                  <CallsToAction
                    actionItems={stack.sidebar_cta_references}
                    align="flex-start"
                    stacked
                  />
                </StickyNav>
              </PrerenderSafeLazyLoad>
            </Grid>
            <Grid
              item
              md={8}
              xs={12}
              data-swiftype-name="body"
              data-swiftype-type="text"
            >
              {stack.sections.map((section, idx) => (
                <HubSection key={idx} name={section.title}>
                  <PrerenderSafeLazyLoad>
                    <HubSectionHeader
                      key={idx}
                      backgroundimg={
                        section.background_image && section.background_image.url
                      }
                    >
                      <HubSectionTitle variant="h3" component="h2" gutterBottom>
                        {section.title}
                      </HubSectionTitle>
                    </HubSectionHeader>
                    <ContentTypography
                      variant="body2"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                    <aside data-swiftype-index="false">
                      {section.resource_inline.resource_reference?.length >
                        0 && (
                        <InlineResourceCTA
                          title={section.resource_inline.title}
                          resource={
                            section.resource_inline.resource_reference[0]
                          }
                          size={section.resource_inline.size}
                        />
                      )}

                      {section.blog_inline.blog_reference?.length > 0 && (
                        <InlineBlogCTA
                          title={section.blog_inline.title}
                          post={section.blog_inline.blog_reference[0]}
                          size={section.blog_inline.size}
                        />
                      )}
                      {section.cta_references?.length > 0 &&
                        section.cta_references.map(cta => (
                          <>
                            {cta._content_type_uid === 'card_cta' && (
                              <InlineCTA size="wide" {...cta} />
                            )}

                            {cta._content_type_uid === 'photo_cta' && (
                              <PhotoCTA {...cta} />
                            )}

                            {cta._content_type_uid === 'bold_cta' && (
                              <BoldCTA {...cta} />
                            )}
                          </>
                        ))}
                    </aside>
                  </PrerenderSafeLazyLoad>
                </HubSection>
              ))}
            </Grid>
            <Grid item md={1} xs={12}>
              <PrerenderSafeLazyLoad>
                <SocialShare url={window.location.href} />
              </PrerenderSafeLazyLoad>
            </Grid>
          </SectionGrid>
        </Container>
        {stack.footer_ctas && (
          <PrerenderSafeLazyLoad>
            <FooterCTAs ctas={stack.footer_ctas} />
          </PrerenderSafeLazyLoad>
        )}
      </Fragment>
    ) : null;
  }
}

Hub.propTypes = {
  stack: PropTypes.shape({
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    sections: PropTypes.array.isRequired,
    footer_ctas: PropTypes.object.isRequired
  })
};

export default connect(
  state => ({
    stack: state.hub.stack,
    loading: state.hub.loading
  }),
  { getHubStack, setTopNavColorLight }
)(Hub);
